import { Box } from '@mui/material';
import { KeyboardEvent, useState } from 'react';
import BaseInput from '../../../../../components/v2/form/BaseInput/BaseInput';
import Typography from '../../../../../components/v2/ui/Typography/Typography';
import LangEnum from '../../../../../lang/LangEnum';
import useTeceoStyles from '../../../../../useTeceoStyles';
import { CatalogProductsTestIds } from '../../../../../utils/testIds/CatalogProducts.testIds';
import { useTranslation } from '../../../../translation/TranslationContext';

interface CatalogSectionHeaderV2Props {
  sectionName: string;
  groupIndex: number;
  onRename?: (name: string) => void;
}

const CatalogSectionHeaderV2 = ({
  sectionName,
  groupIndex,
  onRename,
}: CatalogSectionHeaderV2Props) => {
  const { classes: teceoClasses } = useTeceoStyles();
  const [isEditing, setIsEditing] = useState(false);

  const handleCommit = (name: string) => {
    setIsEditing(false);
    const trimmed = name.trim();

    if (trimmed.length > 0 && trimmed !== sectionName) {
      onRename?.(trimmed);
    }
  };

  return (
    <Box
      test-id={CatalogProductsTestIds.SECTION_HEADER}
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backgroundColor: 'background.paper',
        display: 'flex',
        alignItems: 'baseline',
        gap: 1,
        paddingX: 1,
        paddingTop: groupIndex === 0 ? 1.5 : 3,
        paddingBottom: 1.5,
      }}
    >
      {isEditing ? (
        <CatalogSectionNameInput
          initialName={sectionName}
          onCommit={handleCommit}
          onCancel={() => setIsEditing(false)}
        />
      ) : (
        <Typography
          className={teceoClasses.truncate}
          variant="subtitle2"
          testId={CatalogProductsTestIds.SECTION_HEADER_NAME}
          sx={onRename ? { cursor: 'text' } : undefined}
          onClick={onRename ? () => setIsEditing(true) : undefined}
        >
          {sectionName}
        </Typography>
      )}
    </Box>
  );
};

interface CatalogSectionNameInputProps {
  initialName: string;
  onCommit: (name: string) => void;
  onCancel: () => void;
}

const CatalogSectionNameInput = ({
  initialName,
  onCommit,
  onCancel,
}: CatalogSectionNameInputProps) => {
  const { translate } = useTranslation();
  const [value, setValue] = useState(initialName);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onCommit(value);
      return;
    }

    if (event.key === 'Escape') {
      event.stopPropagation();
      onCancel();
    }
  };

  return (
    <Box
      data-value={value}
      sx={(theme) => ({
        display: 'inline-grid',
        maxWidth: '100%',
        minWidth: '2ch',
        marginX: -0.5,
        paddingX: 0.5,
        borderRadius: `${theme.shape.borderRadiusSm}px`,
        backgroundColor: theme.palette.action.hover,
        '&::after': {
          ...theme.typography.subtitle2,
          content: 'attr(data-value)',
          gridArea: '1 / 1',
          visibility: 'hidden',
          whiteSpace: 'pre',
          paddingRight: '2px',
        },
        '& .MuiInputBase-root': { gridArea: '1 / 1' },
        '& .MuiInputBase-input': { padding: 0, minWidth: 0, width: 0, flex: '1 1 0' },
      })}
    >
      <BaseInput
        autoFocus
        value={value}
        testId={CatalogProductsTestIds.SECTION_HEADER_NAME_INPUT}
        ariaLabel={translate(LangEnum.CATALOGS_SECTIONS_RENAME)}
        sx={(theme) => ({ ...theme.typography.subtitle2 })}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => onCommit(value)}
      />
    </Box>
  );
};

export default CatalogSectionHeaderV2;
